import { Component, OnInit } from '@angular/core';
import { formatDate } from '@angular/common';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { NotificationsService, Severity } from 'app/services/notifications-service/notifications.service';
import { Rest } from 'app/rest/rest_client';
import { TranslateService } from '@ngx-translate/core';
import { ReportBuilderService } from 'app/rest/report-builder.service';
import {
	EntitySchema,
	OneNMode,
	ReportEntityKey,
	ReportFilter,
	SavedReport,
	isBooleanField,
	isDateField,
	reportLabelKey,
	reportSchema,
} from './report-builder-schema';

type WizardStep = -1 | 0 | 1 | 2 | 3;

@Component({
	selector: 'app-report-builder',
	templateUrl: './report-builder.component.html',
	styleUrls: ['./report-builder.component.css'],
	standalone: false,
})
export class ReportBuilderComponent implements OnInit {
	readonly schema = reportSchema;
	readonly entityKeys = Object.keys(reportSchema) as ReportEntityKey[];
	readonly filterOperators: { label: string; value: ReportFilter['op'] }[] = [
		{ label: '=', value: '=' },
		{ label: '≠', value: '!=' },
		{ label: '≥', value: '>=' },
		{ label: '≤', value: '<=' },
		{ label: 'Contiene', value: 'contiene' },
	];

	step: WizardStep = -1;
	entity: ReportEntityKey | null = null;
	fields: string[] = [];
	filters: ReportFilter[] = [];
	oneNModes: OneNMode = {};
	detailEntity = '';
	reportName = '';
	/** id of the saved report being edited; undefined while building a new one. Saves always go by id, never by name. */
	currentReportId: number | undefined;
	showPreview = false;
	loadingPreview = false;
	previewRows: { [field: string]: string }[] = [];
	previewTotalCount = 0;

	saved: SavedReport[] = [];

	constructor(
		private notificationsService: NotificationsService,
		private reportBuilderService: ReportBuilderService,
		private translate: TranslateService,
	) {}

	ngOnInit(): void {
		this.loadSavedReports();
	}

	//#region translations
	// Labels (fields, groups, entities) are identifiers shared with the backend and stay untouched; only what is shown is translated.

	fieldKey(label: string): string {
		return 'reportBuilder.field.' + reportLabelKey(label);
	}

	groupKey(label: string): string {
		return 'reportBuilder.group.' + reportLabelKey(label);
	}

	/** Icono (SVG del menu lateral) de cada entidad del generador. */
	entityIconSrc(key: string): string {
		return key === 'conductor' ? 'assets/icons/person.svg' : 'assets/icons/vehicle-inventory.svg';
	}

	entityKey(entity: string): string {
		return 'reportBuilder.entity.' + entity;
	}

	fieldText(label: string): string {
		return this.translate.instant(this.fieldKey(label));
	}

	private msg(key: string, params?: object): string {
		return this.translate.instant('reportBuilder.messages.' + key, params);
	}

	/** Selected fields, translated, for the result summary. */
	fieldsText(): string {
		return this.fields.map((f) => this.fieldText(f)).join(' · ');
	}

	/** Options of the filter field dropdown: translated label, schema label as value. */
	filterFieldOptions(): { label: string; value: string }[] {
		return this.getAllFilterableFields().map((f) => ({ label: this.fieldText(f), value: f }));
	}

	/** Operators of a filter row: a yes/no field only makes sense with = and !=. */
	filterOperatorOptions(field?: string): { label: string; value: ReportFilter['op'] }[] {
		return this.filterOperators
			.filter((o) => !field || !isBooleanField(field) || o.value === '=' || o.value === '!=')
			.map((o) => (o.value === 'contiene' ? { ...o, label: this.translate.instant('reportBuilder.filters.opContains') } : o));
	}

	/** Yes/No dropdown for the boolean fields; the values are the ones the backend returns. */
	booleanOptions(): { label: string; value: string }[] {
		return [
			{ label: this.translate.instant('reportBuilder.filters.yes'), value: 'Si' },
			{ label: this.translate.instant('reportBuilder.filters.no'), value: 'No' },
		];
	}

	/** The previous value and operator may not make sense for the newly picked field. */
	onFilterFieldChange(filter: ReportFilter): void {
		filter.value = '';
		if (filter.field && isBooleanField(filter.field) && filter.op !== '=' && filter.op !== '!=') {
			filter.op = '=';
		}
	}

	/** Value of a filter as displayed: yes/no values are translated. */
	filterValueText(filter: ReportFilter): string {
		if (filter.field && isBooleanField(filter.field)) {
			const option = this.booleanOptions().find((o) => o.value === filter.value);
			if (option) return option.label;
		}
		return filter.value;
	}

	//#endregion

	//#region navigation

	goHome(): void {
		this.step = -1;
		this.resetSelection();
	}

	goStep(step: WizardStep): void {
		this.step = step;
		this.showPreview = false;
	}

	goStepSafe(step: WizardStep): void {
		if (step <= this.step || (step === 1 && this.entity) || (step >= 2 && this.fields.length > 0)) {
			this.goStep(step);
		}
	}

	canGoNext(): boolean {
		if (this.step === 0) return !!this.entity;
		if (this.step === 1) return this.fields.length > 0;
		return true;
	}

	private resetSelection(): void {
		this.entity = null;
		this.fields = [];
		this.filters = [];
		this.oneNModes = {};
		this.detailEntity = '';
		this.reportName = '';
		this.currentReportId = undefined;
		this.showPreview = false;
		this.previewRows = [];
	}

	//#endregion

	/** Keeps `keyvalue` pipe iteration in declaration order instead of alphabetically sorting keys. */
	originalOrder = (): number => 0;

	//#region step 0: entity

	get currentSchema(): EntitySchema | null {
		return this.entity ? this.schema[this.entity] : null;
	}

	selectEntity(entity: ReportEntityKey): void {
		this.entity = entity;
		this.fields = [];
		this.filters = [];
		this.oneNModes = {};
		this.detailEntity = '';
	}

	entityFieldCount(entity: ReportEntityKey): number {
		const s = this.schema[entity];
		let count = 0;
		Object.values(s.groups).forEach((fs) => (count += fs.length));
		Object.values(s.oneN).forEach((o) => (count += o.summary.length + o.detail.length));
		return count;
	}

	entityCategoryCount(entity: ReportEntityKey): number {
		const s = this.schema[entity];
		return Object.keys(s.groups).length + Object.keys(s.oneN).length;
	}

	//#endregion

	//#region step 1: fields

	isFieldSelected(field: string): boolean {
		return this.fields.indexOf(field) >= 0;
	}

	toggleField(field: string): void {
		const idx = this.fields.indexOf(field);
		if (idx >= 0) this.fields.splice(idx, 1);
		else this.fields.push(field);
	}

	isGroupFullySelected(groupFields: string[]): boolean {
		return groupFields.every((f) => this.isFieldSelected(f));
	}

	toggleGroup(groupFields: string[]): void {
		const allSelected = this.isGroupFullySelected(groupFields);
		if (allSelected) {
			groupFields.forEach((f) => {
				const idx = this.fields.indexOf(f);
				if (idx >= 0) this.fields.splice(idx, 1);
			});
		} else {
			groupFields.forEach((f) => {
				if (this.fields.indexOf(f) < 0) this.fields.push(f);
			});
		}
	}

	oneNMode(subEntity: string): 'none' | 'summary' | 'detail' {
		return this.oneNModes[subEntity] || 'none';
	}

	canUseDetailMode(subEntity: string): boolean {
		return !this.detailEntity || this.detailEntity === subEntity;
	}

	setOneNMode(subEntity: string, mode: 'none' | 'summary' | 'detail'): void {
		if (!this.currentSchema) return;
		const previousMode = this.oneNMode(subEntity);
		if (previousMode !== 'none') {
			const oldFields = previousMode === 'summary'
				? this.currentSchema.oneN[subEntity].summary
				: this.currentSchema.oneN[subEntity].detail;
			oldFields.forEach((f) => {
				const idx = this.fields.indexOf(f);
				if (idx >= 0) this.fields.splice(idx, 1);
			});
		}
		if (previousMode === 'detail' && this.detailEntity === subEntity) {
			this.detailEntity = '';
		}
		if (mode === 'none') {
			delete this.oneNModes[subEntity];
		} else {
			this.oneNModes[subEntity] = mode;
			if (mode === 'detail') this.detailEntity = subEntity;
		}
	}

	//#endregion

	//#region step 2: filters

	getAllFilterableFields(): string[] {
		if (!this.currentSchema) return [];
		const all: string[] = [];
		Object.values(this.currentSchema.groups).forEach((fs) => fs.forEach((f) => {
			if (all.indexOf(f) < 0) all.push(f);
		}));
		Object.keys(this.currentSchema.oneN).forEach((subEntity) => {
			const mode = this.oneNMode(subEntity);
			if (mode === 'none') return;
			const arr = mode === 'summary' ? this.currentSchema.oneN[subEntity].summary : this.currentSchema.oneN[subEntity].detail;
			arr.forEach((f) => {
				if (all.indexOf(f) < 0) all.push(f);
			});
		});
		return all;
	}

	isDateField = isDateField;
	isBooleanField = isBooleanField;

	addFilter(): void {
		this.filters.push({ field: '', op: '=', value: '' });
	}

	removeFilter(index: number): void {
		this.filters.splice(index, 1);
	}

	//#endregion

	//#region step 3: result

	get activeFilters(): ReportFilter[] {
		return this.filters.filter((f) => f.field && f.value);
	}

	generatePreview(): void {
		this.loadingPreview = true;
		this.showPreview = true;
		// Reuses the same shape/serialization as a saved report (toRest()); id/name/user are
		// irrelevant for a one-off preview, so this works whether the report was saved or not.
		const report = this.toRest({
			name: this.reportName || '',
			entity: this.entity as ReportEntityKey,
			fields: [...this.fields],
			filters: this.activeFilters.map((f) => ({ ...f })),
			oneNModes: { ...this.oneNModes },
			detailEntity: this.detailEntity,
		});
		this.reportBuilderService.generate(report).then(
			(result: Rest.ReportBuilderQueryResult) => {
				this.previewRows = result.rows || [];
				this.previewTotalCount = result.total || 0;
				this.loadingPreview = false;
			},
			(error: any) => {
				console.log('ReportBuilder generate failed: ' + error);
				this.previewRows = [];
				this.previewTotalCount = 0;
				this.loadingPreview = false;
				this.notificationsService.add(Severity.error, this.msg('title'), this.msg('generateError'));
			}
		);
	}

	/** Header (translated) and rows of what is shown, in the order of the selected fields. */
	private exportTable(): { headers: string[]; rows: string[][] } {
		return {
			headers: this.fields.map((f) => this.fieldText(f)),
			rows: this.previewRows.map((row) => this.fields.map((f) => (row[f] == null ? '' : row[f].toString()))),
		};
	}

	private exportFileName(extension: string): string {
		const name = (this.reportName || this.translate.instant('reportBuilder.result.defaultFile')).trim();
		return `${name}_export_${formatDate(new Date(), 'yyyyMMddHHmmss', 'en-US')}.${extension}`;
	}

	exportExcel(): void {
		if (!this.previewRows.length) return;
		const { headers, rows } = this.exportTable();
		// The cells stay as text on purpose: codes such as 00000008 must keep their leading zeros.
		const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
		ws['!cols'] = headers.map((header, col) => ({
			wch: Math.min(40, Math.max(header.length, ...rows.slice(0, 200).map((r) => r[col].length))) + 2,
		}));
		const wb: XLSX.WorkBook = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, this.translate.instant('reportBuilder.result.defaultName'));
		XLSX.writeFile(wb, this.exportFileName('xlsx'));
	}

	exportPdf(): void {
		if (!this.previewRows.length) return;
		const { headers, rows } = this.exportTable();
		// Same look as the reports wizard: landscape when there are many columns, red header.
		const doc = headers.length > 5 ? new jsPDF('landscape') : new jsPDF('p', 'pt');
		doc['autoTable']({
			head: [headers],
			body: rows,
			margin: { top: 5, right: 5, bottom: 5, left: 5 },
			theme: 'grid',
			styles: { overflow: 'linebreak', fontSize: 8 },
			headStyles: { fillColor: [220, 53, 69], textColor: [255, 255, 255], fontSize: 7, overflow: 'linebreak' },
			tableWidth: 'wrap',
			horizontalPageBreak: true,
		});
		doc.save(this.exportFileName('pdf'));
	}

	//#endregion

	//#region saved reports (persisted server-side via ReportBuilderService)

	private loadSavedReports(): void {
		this.reportBuilderService.findMine().then(
			(definitions: Rest.ReportBuilder[]) => {
				this.saved = definitions.map((d) => this.fromRest(d));
			},
			(error: any) => {
				console.log('ReportBuilderDefinition findMine failed: ' + error);
				this.saved = [];
			}
		);
	}

	private toRest(report: SavedReport): Rest.ReportBuilder {
		return {
			id: report.id || 0,
			name: report.name,
			entity: report.entity,
			fields: JSON.stringify(report.fields),
			filters: JSON.stringify(report.filters),
			oneNModes: JSON.stringify(report.oneNModes),
			detailEntity: report.detailEntity,
			user: null,
		};
	}

	private fromRest(definition: Rest.ReportBuilder): SavedReport {
		return {
			id: definition.id,
			name: definition.name,
			entity: definition.entity as ReportEntityKey,
			fields: definition.fields ? JSON.parse(definition.fields) : [],
			filters: definition.filters ? JSON.parse(definition.filters) : [],
			oneNModes: definition.oneNModes ? JSON.parse(definition.oneNModes) : {},
			detailEntity: definition.detailEntity || '',
		};
	}

	saveReport(): void {
		const name = (this.reportName || '').trim();
		if (!name) {
			this.notificationsService.add(Severity.warn, this.msg('title'), this.msg('nameRequired'));
			return;
		}
		const report: SavedReport = {
			id: this.currentReportId,
			name,
			entity: this.entity as ReportEntityKey,
			fields: [...this.fields],
			filters: this.filters.map((f) => ({ ...f })),
			oneNModes: { ...this.oneNModes },
			detailEntity: this.detailEntity,
		};
		this.reportBuilderService.save(this.toRest(report)).then(
			(response: Rest.ReportBuilder) => {
				const saved = this.fromRest(response);
				this.currentReportId = saved.id;
				const existingIndex = this.saved.findIndex((r) => r.id === saved.id);
				if (existingIndex >= 0) this.saved[existingIndex] = saved;
				else this.saved.push(saved);
				this.notificationsService.add(Severity.success, this.msg('title'), this.msg('saved', { name }));
			},
			(error: any) => {
				console.log('ReportBuilderDefinition save failed: ' + error);
				this.notificationsService.add(Severity.error, this.msg('title'), this.msg('saveError'));
			}
		);
	}

	/**
	 * Reports saved with older labels (e.g. with accents: "Matrícula") don't match the current
	 * schema labels, so their chips never show as selected and can't be deselected, yet they keep
	 * being sent on every save. Map each saved label onto the current schema (ignoring accents and
	 * case) and drop the ones that no longer exist.
	 */
	private normalizeLabel(label: string): string {
		return (label || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
	}

	private labelResolver(entity: ReportEntityKey): (label: string) => string | null {
		const schema = this.schema[entity];
		const known: string[] = [];
		Object.values(schema.groups).forEach((fs) => known.push(...fs));
		Object.keys(schema.oneN).forEach((sub) => {
			known.push(sub, ...schema.oneN[sub].summary, ...schema.oneN[sub].detail);
		});
		const byNormalized: { [normalized: string]: string } = {};
		known.forEach((k) => (byNormalized[this.normalizeLabel(k)] = byNormalized[this.normalizeLabel(k)] || k));
		return (label: string) => (known.indexOf(label) >= 0 ? label : byNormalized[this.normalizeLabel(label)] || null);
	}

	loadReport(saved: SavedReport): void {
		const resolve = this.labelResolver(saved.entity);
		const unique = (list: string[]) => list.filter((f, i) => list.indexOf(f) === i);
		this.entity = saved.entity;
		this.fields = unique(saved.fields.map(resolve).filter((f): f is string => !!f));
		this.filters = saved.filters
			.map((f) => ({ ...f, field: resolve(f.field) || '' }))
			.filter((f) => f.field);
		this.oneNModes = {};
		Object.keys(saved.oneNModes).forEach((sub) => {
			const resolved = resolve(sub);
			if (resolved) this.oneNModes[resolved] = saved.oneNModes[sub];
		});
		this.detailEntity = (saved.detailEntity && resolve(saved.detailEntity)) || '';
		this.reportName = saved.name;
		this.currentReportId = saved.id;
		this.showPreview = false;
		this.step = 2;
	}

	deleteSaved(index: number, event: Event): void {
		event.stopPropagation();
		const report = this.saved[index];
		if (!report.id) {
			this.saved.splice(index, 1);
			return;
		}
		this.reportBuilderService.delete(report.id).then(
			() => {
				this.saved.splice(index, 1);
				if (this.currentReportId === report.id) this.currentReportId = undefined;
			},
			(error: any) => {
				console.log('ReportBuilderDefinition delete failed: ' + error);
				this.notificationsService.add(Severity.error, this.msg('title'), this.msg('deleteError'));
			}
		);
	}

	//#endregion
}
